import { useState, useEffect } from "react";
import { EVENT5K } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Award, Handshake, Heart } from "lucide-react";

const TIERS = [
  { key: "champion", label: "Champion", price: "$5,000+", color: "#EC008C", perks: ["Premier logo placement", "Stage recognition", "Vendor table + booth", "Social media takeover", "10 race entries", "Featured in press"] },
  { key: "empower", label: "Empower", price: "$2,500", color: "#00A651", perks: ["Prominent logo placement", "Vendor table", "Social media recognition", "5 race entries", "Website + program feature"] },
  { key: "community", label: "Community", price: "$1,000", color: "#00A651", perks: ["Logo on event shirt", "Vendor space", "Social media shoutout", "3 race entries"] },
  { key: "friend", label: "Friend", price: "$500", color: "#EC008C", perks: ["Logo on website", "Social media mention", "2 race entries"] },
];

const INTERESTS = [
  { key: "general", label: "General sponsorship" },
  { key: "warm_up", label: "Lead the Warm-Up" },
  { key: "gym_team", label: "Bring a Gym Team" },
  { key: "cheer_zone", label: "Sponsor a Cheer Zone" },
  { key: "raffle", label: "Donate a Raffle Item" },
  { key: "wellness_table", label: "Host a Wellness Table" },
  { key: "fuel_partner", label: "Fuel / Hydration Partner" },
  { key: "team_captain", label: "Team Captain" },
];

const empty = { company_name: "", contact_name: "", email: "", phone: "", sponsorship_level: "champion", interest: "general", message: "" };

export function SponsorDialog({ open, onOpenChange, initialLevel, initialInterest }) {
  const [form, setForm] = useState({ ...empty, sponsorship_level: initialLevel || "champion", interest: initialInterest || "general" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Reset form when reopening with new preselects
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, sponsorship_level: initialLevel || f.sponsorship_level, interest: initialInterest || f.interest }));
    }
  }, [open, initialLevel, initialInterest]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Please fill required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sponsors", form);
      setDone(true);
      toast.success("Sponsorship inquiry received!");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setDone(false); setForm(empty); }}}>
      <DialogContent data-testid={EVENT5K.sponsorDialog} className="max-w-2xl bg-[#0A0A0A] border-[#27272A] text-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00A651] font-bold">Become a Sponsor</div>
          <DialogTitle className="font-display text-3xl uppercase">Fuel the movement.</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            Sponsor levels: Champion, Empower, Community, Friend, or custom. We&apos;ll follow up with the deck.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-4">
            <div className="bg-[#141414] border border-[#00A651]/50 p-6">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Inquiry received</div>
              <p className="mt-2 text-[#E5E5E5]">A team member will follow up with the sponsorship deck and next steps.</p>
            </div>
            <button className="mt-6 border-2 border-white/40 hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-sm px-6 py-3" onClick={() => onOpenChange(false)}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Company name" required>
                <Input data-testid={EVENT5K.sponsorCompany} value={form.company_name} onChange={(e) => update("company_name", e.target.value)} required />
              </Field>
              <Field label="Contact name" required>
                <Input data-testid={EVENT5K.sponsorContact} value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} required />
              </Field>
              <Field label="Email" required>
                <Input data-testid={EVENT5K.sponsorEmail} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </Field>
              <Field label="Sponsorship level">
                <Select value={form.sponsorship_level} onValueChange={(v) => update("sponsorship_level", v)}>
                  <SelectTrigger data-testid={EVENT5K.sponsorLevel} className="bg-black border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#27272A] text-white">
                    {TIERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label} · {t.price}</SelectItem>)}
                    <SelectItem value="custom">Custom / Let&apos;s talk</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="I'm interested in">
                <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                  <SelectTrigger data-testid="sponsor-interest" className="bg-black border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#27272A] text-white">
                    {INTERESTS.map((i) => <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Message">
              <Textarea rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} />
            </Field>
            <button data-testid={EVENT5K.sponsorSubmit} disabled={submitting} className="w-full bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm py-4 transition-all">
              {submitting ? "Submitting…" : "Send Sponsorship Inquiry →"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SponsorSection({ onApply }) {
  const iconMap = { champion: Trophy, empower: Award, community: Handshake, friend: Heart };

  return (
    <section id="sponsor" data-testid={EVENT5K.sectionSponsor} className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00A651] font-bold mb-4">Sponsorship Opportunities</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
            Sponsors <span className="text-[#EC008C]">empower</span> entrepreneurs.
          </h2>
          <p className="mt-4 text-lg text-[#A3A3A3]">
            Put your brand at the center of a growing community campaign. Logo placement, table space, social recognition, and event-day visibility — customized to your goals.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => {
            const Icon = iconMap[t.key] || Trophy;
            return (
              <div key={t.key} className="bg-[#141414] border border-[#27272A] p-6 flex flex-col hover:border-white/40 transition-colors">
                <Icon className="w-6 h-6 mb-4" style={{ color: t.color }} />
                <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: t.color }}>{t.label}</div>
                <div className="mt-1 font-display text-4xl font-black text-white">{t.price}</div>
                <ul className="mt-4 space-y-1.5 text-sm text-[#E5E5E5] flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span className="mt-2 w-1 h-1" style={{ background: t.color }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onApply(t.key)}
                  data-testid={EVENT5K.sponsorApplyBtn + `-${t.key}`}
                  className="mt-5 font-bold uppercase tracking-widest text-xs border-2 py-2.5 transition-all hover:-translate-y-0.5"
                  style={{ borderColor: t.color, color: t.color }}
                >
                  Choose {t.label}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center">
          <button
            data-testid={EVENT5K.sponsorApplyBtn}
            onClick={() => onApply("custom")}
            className="bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-0.5"
          >
            Become a Sponsor →
          </button>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]">
        {label} {required && <span className="text-[#EC008C]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
